import { router, useLocalSearchParams } from 'expo-router';
import { produce } from 'immer';
import cloneDeep from 'lodash/cloneDeep';
import { useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useCartActions, useCartValues } from '@fhs/cart/src/state/cartStore';
import type { Schema } from '@fhs/client';
import { isWeb } from '@fhs/utils';
import { create, devtools } from '@fhs/zustand';
import { IIncentiveSelection } from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import { parseEntry } from '@fhs-legacy/frontend/src/state/global-state/models/loyalty/offers/offers.utils';
import { routes } from '@fhs-legacy/frontend/src/utils/routing';
import { ICartEntry } from '@rbi-ctg/menu';

import { useAddIncentiveToCart } from '../hooks/use-add-incentive-to-cart';
import { useOfferDetailUi } from '../queries/loyalty.queries';

type OfferGuideCartState = {
  onBackAttempt?: () => boolean;
  offerInfo?: Omit<SimplyOfferType, 'steps'>;
  steps: Step[];
  currentStepIndex: number;
  isGoingForward: boolean;
  isEdit: boolean;
  showSummary: boolean;
  selectedEligibleItem?: Schema['MenuSectionItem']['type'];
  selectedTierId?: string;
  enableSimplifiedMenuDetailView: boolean;
};

type GoToStepParams = {
  index: number;
  immediate?: boolean;
  isEdit?: boolean;
};

type OfferGuideCartActions = {
  modifyItemSelectionInStep: (step: number, cartEntry: ICartEntry) => void;
  goToStep: ({ index, immediate, isEdit }: GoToStepParams) => void;
  goToPrevStep: () => void;
  goToNextStep: () => void;
  goToNextNotDoneStep: () => void;
  goFromScratch: () => void;
  applyPreSelectedItems: (cartEntries: ICartEntry[]) => void;
  setOffer: (offer: SimplyOfferType, enableSimplifiedMenuDetailView?: boolean) => void;
  setSelectedEligibleItem: (
    eligibleItem?: Schema['MenuSectionItem']['type'],
    isEdit?: boolean
  ) => void;
  setSelectedTierId: (tierId?: string) => void;
  reset: () => void;
  setOnBackAttempt: (callback: (() => boolean) | undefined) => void;
};

const initialState: OfferGuideCartState = {
  onBackAttempt: undefined,
  offerInfo: undefined,
  steps: [],
  currentStepIndex: 0,
  isGoingForward: true,
  isEdit: false,
  showSummary: false,
  selectedEligibleItem: undefined,
  selectedTierId: undefined,
  enableSimplifiedMenuDetailView: false,
};

export type Step = Schema['Step']['type'] & {
  selectedEntry?: ICartEntry;
  selectedEntryImage?: string;
};

export type SimplyOfferType = Schema['OfferDetail']['type'] & {
  steps: Step[];
};

const _isValidIndex = (newIndex: number, stepsLength: number) => {
  if (newIndex < 0 || newIndex >= stepsLength) {
    return false;
  }

  return true;
};

const _getNextNotDoneStep = (steps: Step[]) => {
  const lastIndexWithoutProduct = steps.findIndex(step => !step.selectedEntry);

  if (lastIndexWithoutProduct === -1) {
    return steps.length;
  }
  return lastIndexWithoutProduct;
};

const addCartEntryUrl = (
  cartEntry: ICartEntry,
  enableSimplifiedMenuDetailView: boolean,
  eligibleItem?: Schema['MenuSectionItem']['type']
) => {
  return {
    ...cartEntry,
    url:
      (enableSimplifiedMenuDetailView ? eligibleItem?.url : eligibleItem?._legacyUrl) ||
      cartEntry.url,
  } as ICartEntry;
};

export const useOfferCart = create<OfferGuideCartState & OfferGuideCartActions>()(
  devtools((set, get) => {
    const goToStep = ({ index, immediate = false, isEdit = false }: GoToStepParams) => {
      const customSetTimeout = immediate ? r => r() : setTimeout;
      const state = get();
      const stepsLength = state.steps.length;
      if (index === stepsLength) {
        customSetTimeout(() => {
          set({
            currentStepIndex: index,
            showSummary: true,
            isEdit: false,
            selectedEligibleItem: undefined,
          });
        }, 1);
        set({ isGoingForward: true });
        return;
      }

      const step = state.steps[index];
      const selectedEligibleItem = isEdit
        ? step?.eligibleItems?.find(eligibleItem => eligibleItem.id === step.selectedEntry?._id)
        : undefined;

      if (_isValidIndex(index, stepsLength)) {
        customSetTimeout(() => {
          set({
            currentStepIndex: index,
            showSummary: false,
            isEdit: isEdit && !!selectedEligibleItem,
            selectedEligibleItem,
          });
        }, 1);
        if (index < state.currentStepIndex) {
          set({ isGoingForward: false });
          return;
        }
        set({ isGoingForward: true });
      }
    };
    return {
      ...initialState,
      reset: () => set(initialState),
      setSelectedEligibleItem: (eligibleItem?: Schema['MenuSectionItem']['type'], isEdit = false) =>
        set({ selectedEligibleItem: eligibleItem, isEdit }),
      setOnBackAttempt: (callback: (() => boolean) | undefined) => set({ onBackAttempt: callback }),
      goToNextStep: () => goToStep({ index: get().currentStepIndex + 1 }),
      goToNextNotDoneStep: () => goToStep({ index: _getNextNotDoneStep(get().steps) }),
      goToPrevStep: () => goToStep({ index: get().currentStepIndex - 1 }),
      goToStep,
      setSelectedTierId: (tierId?: string) =>
        set(state => ({
          selectedTierId: tierId,
          steps: state?.offerInfo?.tiers?.find(tier => tier.id === tierId)?.steps ?? [],
          selectedEligibleItem: undefined,
        })),
      applyPreSelectedItems: (cartEntries: ICartEntry[]) =>
        set(state => {
          const newSteps = produce(state.steps, draftSteps => {
            draftSteps.forEach(draftStep => {
              const selectedEntry = cartEntries.find(
                cartEntry => cartEntry.cartId === draftStep.preSelectedItem
              );
              draftStep.preSelectedItem = undefined;

              if (selectedEntry) {
                const eligibleItem = draftStep.eligibleItems.find(
                  item => item.id === selectedEntry?._id
                );

                draftStep.selectedEntry = addCartEntryUrl(
                  selectedEntry,
                  state.enableSimplifiedMenuDetailView,
                  eligibleItem
                );
                draftStep.selectedEntryImage = eligibleItem?.image?.asset?.uri;
              }
            });
          });

          const nextStepIndex = _getNextNotDoneStep(newSteps);
          goToStep({ index: nextStepIndex, immediate: true });

          return { steps: newSteps };
        }),
      goFromScratch: () =>
        set(state => {
          const newSteps = produce(state.steps, draftSteps => {
            draftSteps.forEach(draftStep => {
              draftStep.preSelectedItem = undefined;
            });
          });

          return { steps: newSteps };
        }),
      setOffer: (offer: SimplyOfferType, enableSimplifiedMenuDetailView = false) => {
        const { steps, ...offerInfo } = offer;
        set({ steps, offerInfo, enableSimplifiedMenuDetailView });
      },
      modifyItemSelectionInStep: (step: number, cartEntry: ICartEntry) =>
        set(state => {
          const newSteps = produce(state.steps, draftSteps => {
            const draftStep = draftSteps[step];
            const eligibleItem = draftStep.eligibleItems?.find(item => item.id === cartEntry._id);

            draftStep.selectedEntry = addCartEntryUrl(
              cartEntry,
              state.enableSimplifiedMenuDetailView,
              eligibleItem
            );
            draftStep.selectedEntryImage = eligibleItem?.image?.asset?.uri;
          });

          const nextStepIndex = _getNextNotDoneStep(newSteps);
          goToStep({ index: nextStepIndex });

          return { steps: newSteps };
        }),
    };
  })
);

export const useIsOfferCartDirty = () => {
  const steps = useOfferCart(state => state.steps);
  const isDirty = useMemo(() => steps.some(step => !!step.selectedEntry), [steps]);

  return isDirty;
};

export const useIsItemInCart = () => {
  const steps = useOfferCart(state => state.steps);
  const isItemInCart = useMemo(() => steps.some(step => !!step.preSelectedItem), [steps]);

  return isItemInCart;
};

export const useSelectedStep = () => {
  const steps = useOfferCart(state => state.steps);
  const currentStepIndex = useOfferCart(state => state.currentStepIndex);
  const selectedStep = useMemo(() => steps[currentStepIndex], [steps, currentStepIndex]);

  return selectedStep;
};

export const useShouldRenderTierSelector = () => {
  const offerInfo = useOfferCart(state => state.offerInfo);
  const selectedTierId = useOfferCart(state => state.selectedTierId);
  return Boolean(offerInfo?.tiers && offerInfo.tiers?.length > 1 && !selectedTierId);
};

export const useEditStepSelectedEntry = () => {
  const goToStep = useOfferCart(state => state.goToStep);

  const editStepSelectedEntry = useCallback(
    (stepIndex: number) => {
      goToStep({ index: stepIndex, isEdit: true });
    },
    [goToStep]
  );

  return { editStepSelectedEntry };
};

export const useAddGuideOfferToCart = () => {
  const { offerInfo, steps: offerSteps, reset, selectedTierId } = useOfferCart();
  const { cartEntries } = useCartValues();
  const { addItemToCart, removeItemFromCart, updateCartItemsQuantity } = useCartActions();

  const { addIncentiveToCart } = useAddIncentiveToCart();

  const getCartEntriesChanges = useCallback(
    (
      originalCartEntries: ICartEntry[],
      newCartEntries: ICartEntry[],
      selectionsCartEntries: ICartEntry[]
    ) => {
      const changes: (() => void)[] = [];

      const newEntriesMap = new Map(newCartEntries.map(entry => [entry.cartId, entry]));
      originalCartEntries.forEach(originalCartEntry => {
        const newCartEntry = newEntriesMap.get(originalCartEntry.cartId);
        if (newCartEntry && newCartEntry.quantity !== originalCartEntry.quantity) {
          if (newCartEntry.quantity <= 0) {
            changes.push(() =>
              removeItemFromCart({ cartId: originalCartEntry.cartId, skipLoyaltyActions: true })
            );
          } else {
            changes.push(() =>
              updateCartItemsQuantity(originalCartEntry.cartId, newCartEntry.quantity)
            );
          }
        }
      });

      selectionsCartEntries.forEach(entry => {
        changes.push(() => addItemToCart(entry, undefined, false));
      });

      return changes;
    },
    [removeItemFromCart, updateCartItemsQuantity, addItemToCart]
  );

  const addGuideOfferToCart = useCallback(
    (dryRun = false) => {
      if (offerInfo) {
        const selections: IIncentiveSelection[] = [];
        const cartEntriesClone = cloneDeep(cartEntries);
        const selectionsCartEntries: ICartEntry[] = [];
        const cartChangesBatch: (() => void)[] = [];
        offerSteps.forEach(step => {
          const selectedEntry = step.selectedEntry;
          if (!selectedEntry) {
            return;
          }

          const itemInCart = cartEntriesClone.find(
            cartEntry => cartEntry.cartId === step.selectedEntry?.cartId
          );

          if (itemInCart && itemInCart.price) {
            const itemClone = cloneDeep(selectedEntry);
            const individualPrice = itemInCart.price
              ? Math.round(itemInCart.price / itemInCart.quantity)
              : 0;
            itemClone.quantity = 1;
            itemClone.price = individualPrice;
            itemClone.cartId = uuidv4();
            itemClone.lineId = itemClone.cartId;
            selectionsCartEntries.push(itemClone);

            selections.push({
              incentiveId: offerInfo.id,
              key: step.key,
              lineId: itemClone?.cartId ?? '',
              quantity: 1,
              rank: step.rank,
              tierKey: selectedTierId,
            });

            itemInCart.quantity--;
            itemInCart.price = itemInCart.quantity * individualPrice;
            return;
          }

          selections.push({
            incentiveId: offerInfo.id,
            key: step.key,
            lineId: selectedEntry?.cartId,
            quantity: 1,
            rank: step.rank,
            tierKey: selectedTierId,
          });
          selectionsCartEntries.push(selectedEntry);
        });

        cartChangesBatch.push(
          ...getCartEntriesChanges(cartEntries, cartEntriesClone, selectionsCartEntries)
        );

        if (!dryRun) {
          addIncentiveToCart({
            offer: offerInfo,
            selections,
            showMessage: true,
            callbacks: {
              onSuccess: async () => {
                reset();
                router.dismiss();
                cartChangesBatch.forEach(change => change());
                setTimeout(() => {
                  if (isWeb) {
                    router.replace(routes.menu);
                  } else {
                    router.navigate(routes.menu);
                  }
                }, 1);
              },
            },
          });
        }

        return {
          selections,
          backendCartEntries: selectionsCartEntries?.reduce(parseEntry(), []),
        };
      }
      return null;
    },
    [
      offerInfo,
      cartEntries,
      offerSteps,
      getCartEntriesChanges,
      selectedTierId,
      addIncentiveToCart,
      reset,
    ]
  );

  return { addGuideOfferToCart };
};

export const useIsLoadingTierSelector = () => {
  const { offerId, tierSelectorRequired } = useLocalSearchParams<{
    offerId: string;
    tierSelectorRequired: string;
  }>();
  const { isLoading } = useOfferDetailUi(offerId);

  return tierSelectorRequired === 'true' && isLoading;
};
